<template>
  <content-panel>
    <template v-slot:search>
      <search-box class="search-box">
        <el-form :inline="true" size="mini" label-width="120px" :model="searchForm">
          <el-form-item label="包件编码">
            <el-input v-model.trim="searchForm.packageCode" placeholder="请输入包件编码" />
          </el-form-item>
          <el-form-item label="包件名称">
            <el-input v-model.trim="searchForm.packageName" placeholder="请输入包件名称" />
          </el-form-item>
          <el-form-item label="预计出货占比天数范围">
            <el-select v-model="searchForm.expectShipDayRange">
              <el-option v-for="item in dayRangeOptions" :key="item" :label="`未来${item}天`" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="完成进度">
            <el-select v-model="searchForm.totalProgressStatusList" multiple collapse-tags clearable>
              <el-option v-for="item in progressStatusOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
        </el-form>
        <template v-slot:right>
          <el-button type="primary" size="mini" @click="onSearch">搜索</el-button>
          <el-button class="orange-btn" size="mini" @click="onReset">重置</el-button>
        </template>
      </search-box>
    </template>

    <table-panel ref="tablePanel">
      <template v-slot:header-left>
        <div class="header-info">
          <span>缺货发起时间：{{ headerInfo.createdDate || '-' }}</span>
          <span>计划回复时间：{{ headerInfo.packCommitDate || '-' }}</span>
        </div>
      </template>

      <el-table
        :data="tableData.list"
        border
        stripe
        v-loading="loading"
        :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
        :cell-style="{ textAlign: 'center' }"
        :height="maxTableHeight"
      >
        <el-table-column prop="packageCode" label="包件编码" min-width="120" show-overflow-tooltip />
        <el-table-column prop="packageName" label="包件名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="totalOosNum" label="缺货总数" min-width="100" />
        <el-table-column prop="urgentOosNum" label="紧急缺货量" min-width="110" />

        <el-table-column label="预计出货量" min-width="120">
          <template slot-scope="{ row }">
            <span v-if="row.packageCode == '合计'">{{ row.expectShipNum }}</span>
            <el-popover trigger="hover" placement="top" width="640" @show="loadDailyData('expect', row)" v-else>
              <div v-if="expectShipDailyMap[row.packageCode]" class="daily-table">
                <div
                  v-for="week in getDailyWeeks(expectShipDailyMap[row.packageCode])"
                  :key="week.weekLabel"
                  class="daily-week-block"
                >
                  <div class="daily-week-title">{{ week.weekLabel }}</div>
                  <div class="daily-row daily-head">
                    <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-label`">{{ day.dateLabel }}</span>
                  </div>
                  <div class="daily-row">
                    <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-value`">{{ day.quantity }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="daily-loading">加载中...</div>
              <el-link slot="reference" :underline="false" type="success">{{ row.expectShipNum }}</el-link>
            </el-popover>
          </template>
        </el-table-column>

        <el-table-column prop="earliestExpectShipTime" label="预计出货时间（最早）" min-width="150" />

        <el-table-column label="预计出货占比">
          <el-table-column prop="nextDayExpectShipNum" :label="`未来${searchForm.expectShipDayRange}日预计出货量`" min-width="150" />
          <el-table-column prop="nextDayExpectShipRatio" :label="`未来${searchForm.expectShipDayRange}日预计出货量占比`" min-width="170" />
        </el-table-column>

        <el-table-column label="下线入库完成情况">
          <el-table-column prop="todayShouldOfflineInstockNum" label="截至今日应下线入库量" min-width="170" />
          <el-table-column label="截至今日实际下线入库量" min-width="180">
            <template slot-scope="{ row }">
              <span v-if="row.packageCode == '合计'">{{ row.todayActualOfflineInstockNum }}</span>
              <el-popover trigger="hover" placement="top" width="640" @show="loadDailyData('offline', row)" v-else>
                <div v-if="actualOfflineDailyMap[row.packageCode]" class="daily-table">
                  <div
                    v-for="week in getDailyWeeks(actualOfflineDailyMap[row.packageCode])"
                    :key="week.weekLabel"
                    class="daily-week-block"
                  >
                    <div class="daily-week-title">{{ week.weekLabel }}</div>
                    <div class="daily-row daily-head">
                      <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-label`">{{ day.dateLabel }}</span>
                    </div>
                    <div class="daily-row">
                      <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-value`">{{ day.quantity }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="daily-loading">加载中...</div>
                <el-link slot="reference" :underline="false" type="success">{{ row.todayActualOfflineInstockNum }}</el-link>
              </el-popover>
            </template>
          </el-table-column>
          <el-table-column prop="todayCompleteProgress" label="截至今日完成进度" min-width="140" />
          <el-table-column prop="totalCompleteProgress" label="总完成进度" min-width="120" />
          <el-table-column prop="totalProgressStatus" label="总进度状态" min-width="120" />
        </el-table-column>
      </el-table>

      <template v-slot:footer>
        <div style="text-align: right">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="tableData.pageNum"
            :page-sizes="[30, 100, 500, 1000]"
            :page-size="tableData.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            background
            :total="tableData.total"
          ></el-pagination>
        </div>
      </template>
    </table-panel>
  </content-panel>
</template>

<script>
import pagePathMixin from '@/utils/page-path-mixin'
import tableMixin from '@/utils/tableMixin'

export default {
  name: 'LackPackagesDetails',
  mixins: [pagePathMixin, tableMixin],
  data () {
    return {
      loading: false,
      dayRangeOptions: Array.from({ length: 28 }, (_, index) => index + 1),
      progressStatusOptions: ['进行中', '已完成(准时)', '已完成(逾期)', '逾期未完成'],
      searchForm: {
        workflowNo: '',
        packageCode: '',
        packageName: '',
        expectShipDayRange: 5,
        totalProgressStatusList: []
      },
      headerInfo: {
        createdDate: '',
        packCommitDate: ''
      },
      tableData: {
        list: [],
        pageNum: 1,
        pageSize: 30,
        total: 0
      },
      expectShipDailyMap: {},
      actualOfflineDailyMap: {}
    }
  },
  mounted () {
    this.headerInfo.createdDate = this.$route.query.createdDate || ''
    this.headerInfo.packCommitDate = this.$route.query.packCommitDate || ''
    this.searchForm.workflowNo = this.$route.query.workflowNo || ''
    this.init()
  },
  methods: {
    init () {
      this.loading = true
      this.$q.orderDate.workflowPackSelectListByPage({
        data: {
          pageNum: this.tableData.pageNum,
          pageSize: this.tableData.pageSize,
          filterData: this.searchForm
        }
      }).then((res) => {
        const data = res.data || {}
        this.tableData = {
          ...this.tableData,
          list: data.list || [],
          total: data.total || 0,
          pageNum: data.pageNum || this.tableData.pageNum,
          pageSize: data.pageSize || this.tableData.pageSize
        }
      }).finally(() => {
        this.loading = false
      })
    },
    onSearch () {
      this.tableData.pageNum = 1
      this.init()
    },
    onReset () {
      this.searchForm = {
        workflowNo: this.$route.query.workflowNo || '',
        packageCode: '',
        packageName: '',
        expectShipDayRange: 5,
        totalProgressStatusList: []
      }
      this.onSearch()
    },
    handleSizeChange (val) {
      this.tableData.pageSize = val
      this.tableData.pageNum = 1
      this.init()
    },
    handleCurrentChange (val) {
      this.tableData.pageNum = val
      this.init()
    },
    loadDailyData (type, row) {
      const targetMap = type === 'expect' ? this.expectShipDailyMap : this.actualOfflineDailyMap
      if (targetMap[row.packageCode]) return
      const apiMethod = type === 'expect' ? 'workflowPackExpectShipDaily' : 'workflowPackActualOfflineDaily'
      this.$q.orderDate[apiMethod]({
        data: {
          packageCode: row.packageCode,
          workflowNo: this.searchForm.workflowNo || ''
        }
      }).then((res) => {
        if (res.success && res.data) {
          this.$set(targetMap, row.packageCode, res.data)
        }
      })
    },
    getDailyWeeks (dailyData) {
      if (!dailyData || !Array.isArray(dailyData.weeks)) return []
      return dailyData.weeks.map((week) => ({
        weekLabel: week.weekLabel || '-',
        days: Array.isArray(week.days) ? week.days : []
      }))
    }
  }
}
</script>

<style lang="stylus" scoped>
.search-box
  >>> .el-select,
  >>> .el-input
    width 220px !important

>>> .date-range .single-date
  width 110px !important

.header-info
  display flex
  gap 16px
  color #606266

.daily-table
  min-width 600px

.daily-week-block
  padding 5px 0
  border-bottom 1px solid #ebeef5

.daily-week-block:last-child
  border-bottom 0

.daily-week-title
  font-weight 600
  color #606266
  margin-bottom 3px

.daily-row
  display flex
  gap 0px
  padding 3px 0
  align-items center
  text-align center

.daily-row span
  flex 1
  min-width 0

.daily-head
  font-weight 600

.daily-loading
  color #909399
  padding 10px 0
</style>
